import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Edit3, Trash2 } from 'lucide-react';
import { TopbarSearch } from '@/components/ui/TopbarSearch';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { CategoryInfoBlock } from './components/CategoryInfoBlock';
import { CategoryStatsBlock } from './components/CategoryStatsBlock';
import { CategorySubTableBlock } from './components/CategorySubTableBlock';
import { CategoryServiceTableBlock } from './components/CategoryServiceTableBlock';
import { useCategoriesDetail } from './hooks/useCategoriesDetail';
import { AuthButton } from '@/core/permissions/AuthButton';

export function CategoriesDetailPage() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const navigate = useNavigate();
  const { 
    category, 
    subCategories, 
    linkedServices, 
    stats, 
    isLoading, 
    isDeleting, 
    deleteCategory 
  } = useCategoriesDetail(id || null);
  
  console.log('[CategoriesDetail] ID:', id, 'Category:', category, 'isLoading:', isLoading);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-600 border-t-transparent" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <p className="text-slate-500">Không tìm thấy danh mục.</p>
        <button 
          onClick={() => navigate('/settings?tab=categories')}
          className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white"
        >
          Quay lại
        </button>
      </div>
    );
  }

  const handleDelete = () => {
    deleteCategory(category.id, {
      onSuccess: () => navigate('/settings?tab=categories')
    });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-20 lg:pb-10">
      {/* Top Header */}
      <header className="sticky top-0 z-10 border-b border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/settings?tab=categories')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-medium text-slate-950">Chi tiết danh mục</h1>
            <p className="truncate text-xs text-slate-500">{category.name}</p>
          </div>

          <div className="flex items-center gap-2">
            <AuthButton
              module="settings.categories"
              action="update"
              behavior="hide"
              onClick={() => navigate(`/settings?tab=categories-edit&id=${category.id}`)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <Edit3 className="h-5 w-5" />
            </AuthButton>
            <AuthButton
              module="settings.categories"
              action="delete"
              behavior="hide"
              onClick={() => setShowDeleteConfirm(true)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
            >
              <Trash2 className="h-5 w-5" />
            </AuthButton>
          </div>
        </div>
      </header>

      <main className="p-3 space-y-4 w-full flex-1 min-w-0">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-4">
            <CategoryInfoBlock category={category} />
            
            <CategorySubTableBlock 
              items={subCategories} 
              onView={(item) => navigate(`?tab=categories-detail&id=${item.id}`)}
              onEdit={(item) => navigate(`?tab=categories-edit&id=${item.id}`)}
              onDelete={(item) => setShowDeleteConfirm(true)} // Or specific delete
            />

            <CategoryServiceTableBlock 
              items={linkedServices}
              onView={(item) => navigate(`/services/detail/${item.id}`)}
              onEdit={(item) => navigate(`/services/edit/${item.id}`)}
              onDelete={() => {}} // Handle service delete
            />
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-4">
            <CategoryStatsBlock stats={stats} />
            
            <div className="rounded-md border border-slate-100 bg-white p-3 shadow-sm space-y-3">
              <h3 className="text-sm font-medium text-slate-950">Thông tin hệ thống</h3>
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">ID hệ thống</span>
                  <span className="font-mono text-slate-700">{category.id.slice(0, 8)}...</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Ngày tạo</span>
                  <span className="text-slate-700">{category.createdAt ? new Date(category.createdAt).toLocaleDateString('vi-VN') : '---'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Cập nhật cuối</span>
                  <span className="text-slate-700">{category.updatedAt ? new Date(category.updatedAt).toLocaleDateString('vi-VN') : '---'}</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Xác nhận xóa"
        message={`Bạn có chắc chắn muốn xóa danh mục "${category.name}"? Tất cả các cấp con cũng sẽ bị xóa.`}
        confirmText="Xóa"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
}
